import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '../../lib/cn';

/**
 * Settings — the two-column settings surface: a grouped section nav on the left, one pane per
 * section on the right, the pane made of label/control rows split by thin dividers.
 *
 * The anatomy follows what current settings UIs converge on (Claude, Linear, Vercel, GitHub;
 * Polaris' app-settings layout puts "glanceable labels and descriptions" in the left column):
 * one pane per nav item rather than one long scroll, so every section is deep-linkable and a
 * long list (Team) does not push the rest out of reach.
 *
 * - **Landmarks.** The nav is a named `<nav>`; each group a labelled `role="group"`; the pane a
 *   `<section>` named by its `<h1>`. Not `<main>` — the app shell already owns that landmark.
 * - **Current item.** `SettingsNavItem active` sets `aria-current="page"` on a real link
 *   (`asChild` around the router's `<Link>`), never a tab.
 * - **Focus on pane switch.** `SettingsPane focusOnMount` moves focus to its `<h1>`
 *   (`tabIndex={-1}`), the pattern route-change studies found screen-reader users expect.
 * - **Narrow screens drill in.** `SettingsLayout mobileView="nav"` shows only the nav as a
 *   full-width list; `"pane"` only the pane, with its `backLink`. From `md` up both show.
 */

export interface SettingsLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  nav: React.ReactNode;
  /** Which column a narrow screen shows. Both columns show from `md` up. */
  mobileView?: 'nav' | 'pane';
}

export const SettingsLayout = React.forwardRef<HTMLDivElement, SettingsLayoutProps>(
  ({ nav, mobileView = 'pane', className, children, ...props }, ref) => (
    <div
      ref={ref}
      data-mobile-view={mobileView}
      className={cn(
        'tw:mx-auto tw:flex tw:w-full tw:max-w-6xl tw:items-start tw:gap-12 tw:px-4 tw:py-6 tw:font-sans tw:text-foreground tw:md:px-8 tw:md:py-10',
        className
      )}
      {...props}
    >
      <aside
        className={cn(
          'tw:w-full tw:md:sticky tw:md:top-6 tw:md:block tw:md:w-56 tw:md:shrink-0',
          mobileView === 'pane' && 'tw:hidden'
        )}
      >
        {nav}
      </aside>
      <div className={cn('tw:min-w-0 tw:flex-1 tw:md:block', mobileView === 'nav' && 'tw:hidden')}>{children}</div>
    </div>
  )
);
SettingsLayout.displayName = 'SettingsLayout';

export interface SettingsNavProps extends React.HTMLAttributes<HTMLElement> {
  /** Accessible name of the navigation landmark, e.g. "Einstellungen". */
  label: string;
  /** Optional visible heading above the groups (the page title on the nav side). */
  heading?: React.ReactNode;
}

export const SettingsNav = React.forwardRef<HTMLElement, SettingsNavProps>(
  ({ label, heading, className, children, ...props }, ref) => (
    <nav ref={ref} aria-label={label} className={cn('tw:flex tw:flex-col tw:gap-6', className)} {...props}>
      {heading && <div className="tw:px-3 tw:text-xl tw:font-semibold tw:tracking-tight">{heading}</div>}
      {children}
    </nav>
  )
);
SettingsNav.displayName = 'SettingsNav';

export interface SettingsNavGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
}

export const SettingsNavGroup = React.forwardRef<HTMLDivElement, SettingsNavGroupProps>(
  ({ label, className, children, ...props }, ref) => {
    const id = React.useId();
    return (
      <div ref={ref} role="group" aria-labelledby={id} className={cn('tw:flex tw:flex-col tw:gap-1', className)} {...props}>
        <p id={id} className="tw:m-0 tw:px-3 tw:text-xs tw:font-medium tw:text-muted-foreground">
          {label}
        </p>
        <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-0.5 tw:p-0">{children}</ul>
      </div>
    );
  }
);
SettingsNavGroup.displayName = 'SettingsNavGroup';

export interface SettingsNavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  /** A leading icon (e.g. a lucide icon at 16px); decorative. */
  icon?: React.ReactNode;
  /** Render the child (the router's `<Link>`) as the item, keeping a real link. */
  asChild?: boolean;
}

export const SettingsNavItem = React.forwardRef<HTMLAnchorElement, SettingsNavItemProps>(
  ({ active = false, icon, asChild = false, className, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return (
      <li className="tw:m-0">
        <Comp
          ref={ref}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'tw:flex tw:items-center tw:gap-2.5 tw:rounded-md tw:px-3 tw:py-2.5 tw:text-sm tw:text-foreground tw:no-underline tw:transition-colors tw:md:py-2',
            // A tint of the foreground, not --accent (= --bg-hover, nearly the page colour): the
            // current item must read as selected in light and dark alike.
            'tw:hover:bg-foreground/[0.04] tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
            active && 'tw:bg-foreground/[0.07] tw:font-medium',
            className
          )}
          {...props}
        >
          {icon && (
            <span aria-hidden="true" className="tw:inline-flex tw:text-muted-foreground">
              {icon}
            </span>
          )}
          <Slottable>{children}</Slottable>
        </Comp>
      </li>
    );
  }
);
SettingsNavItem.displayName = 'SettingsNavItem';

export interface SettingsPaneProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Shown above the title on narrow screens only — the way back to the nav list. */
  backLink?: React.ReactNode;
  /** The pane's one primary action, top-right (e.g. "Mitglied einladen"). */
  actions?: React.ReactNode;
  /** Move focus to the h1 when the pane mounts (a pane switch). Read at mount only. */
  focusOnMount?: boolean;
}

export const SettingsPane = React.forwardRef<HTMLElement, SettingsPaneProps>(
  ({ title, description, backLink, actions, focusOnMount = false, className, children, ...props }, ref) => {
    const id = React.useId();
    const headingRef = React.useRef<HTMLHeadingElement>(null);
    // Read once, at mount: a pane switch mounts the new pane; a later re-render of the same pane
    // must not pull focus back to its heading.
    const focusAtMount = React.useRef(focusOnMount);
    React.useEffect(() => {
      if (focusAtMount.current) headingRef.current?.focus();
    }, []);
    return (
      <section ref={ref} aria-labelledby={id} className={cn('tw:w-full tw:max-w-[44rem]', className)} {...props}>
        {backLink && <div className="tw:mb-4 tw:text-sm tw:md:hidden">{backLink}</div>}
        <header className="tw:mb-8 tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
          <div className="tw:min-w-0">
            <h1
              id={id}
              ref={headingRef}
              tabIndex={-1}
              className="tw:m-0 tw:text-2xl tw:font-semibold tw:tracking-tight tw:text-foreground tw:outline-none"
            >
              {title}
            </h1>
            {description && <p className="tw:mt-1 tw:mb-0 tw:text-sm tw:text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">{actions}</div>}
        </header>
        <div className="tw:flex tw:flex-col tw:gap-10">{children}</div>
      </section>
    );
  }
);
SettingsPane.displayName = 'SettingsPane';

export interface SettingsSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** A section-level control, right-aligned with the heading. */
  action?: React.ReactNode;
  /** `danger` frames the section for irreversible actions (account / farm deletion). */
  tone?: 'default' | 'danger';
  /** Render the rows container as a list (`<ul>`) when its rows are `<li>`. */
  list?: boolean;
  /** Accessible name for the rows list (e.g. "Team"). */
  listLabel?: string;
}

export const SettingsSection = React.forwardRef<HTMLElement, SettingsSectionProps>(
  ({ title, description, action, tone = 'default', list = false, listLabel, className, children, ...props }, ref) => {
    const id = React.useId();
    const Rows = list ? 'ul' : 'div';
    return (
      <section ref={ref} aria-labelledby={title ? id : undefined} className={cn('tw:flex tw:flex-col', className)} {...props}>
        {(title || action) && (
          <div className="tw:mb-1 tw:flex tw:flex-wrap tw:items-end tw:justify-between tw:gap-x-4 tw:gap-y-2">
            <div className="tw:min-w-0">
              {title && (
                <h2 id={id} className="tw:m-0 tw:text-base tw:font-semibold tw:text-foreground">
                  {title}
                </h2>
              )}
              {description && <p className="tw:mt-1 tw:mb-0 tw:text-sm tw:text-muted-foreground">{description}</p>}
            </div>
            {action && <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">{action}</div>}
          </div>
        )}
        <Rows
          aria-label={list ? listLabel : undefined}
          className={cn(
            'tw:m-0 tw:flex tw:list-none tw:flex-col tw:divide-y tw:divide-border tw:p-0',
            tone === 'danger' && 'tw:mt-3 tw:rounded-lg tw:border tw:border-destructive/40 tw:px-4'
          )}
        >
          {children}
        </Rows>
      </section>
    );
  }
);
SettingsSection.displayName = 'SettingsSection';

export interface SettingsRowProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  label: React.ReactNode;
  description?: React.ReactNode;
  /** The id of the control the label names; renders a real `<label>`. */
  htmlFor?: string;
  /** Leading media, e.g. an `Avatar`. */
  media?: React.ReactNode;
  /** Put the control under the label (wide fields, long helper text). */
  stacked?: boolean;
  /** The control is a text field: give it a steady width (full width on phones, 18rem from `sm`). */
  field?: boolean;
  /** `li` inside a `SettingsSection list`. */
  as?: 'div' | 'li';
}

export const SettingsRow = React.forwardRef<HTMLElement, SettingsRowProps>(
  ({ label, description, htmlFor, media, stacked = false, field = false, as = 'div', className, children, ...props }, ref) => {
    const Comp = as as React.ElementType;
    const labelClass = 'tw:block tw:text-sm tw:font-medium tw:text-foreground';
    return (
      <Comp
        ref={ref}
        className={cn(
          'tw:m-0 tw:flex tw:min-h-14 tw:flex-col tw:gap-3 tw:py-4',
          !stacked && 'tw:sm:flex-row tw:sm:items-center tw:sm:justify-between tw:sm:gap-6',
          className
        )}
        {...props}
      >
        <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-3">
          {media}
          <div className="tw:min-w-0">
            {htmlFor ? (
              <label htmlFor={htmlFor} className={labelClass}>
                {label}
              </label>
            ) : (
              <div className={labelClass}>{label}</div>
            )}
            {description && <div className="tw:mt-0.5 tw:text-[13px] tw:leading-5 tw:text-muted-foreground">{description}</div>}
          </div>
        </div>
        {children !== undefined && children !== null && children !== false && (
          <div
            data-field={field || undefined}
            className={cn(
              'tw:flex tw:flex-wrap tw:items-center tw:gap-2',
              !stacked && 'tw:sm:shrink-0 tw:sm:justify-end',
              field && 'tw:w-full tw:sm:w-72 tw:[&>*]:w-full'
            )}
          >
            {children}
          </div>
        )}
      </Comp>
    );
  }
);
SettingsRow.displayName = 'SettingsRow';
